import type { ComponentPropsWithoutRef } from "react";

import { MODAL_ROUTES } from "./router";

export type SVGProps = ComponentPropsWithoutRef<"svg">;

export interface ModalProps {
  show: boolean;
  onClose: () => void;
}

type Flatten<T> = T extends (infer U)[] ? U : T;

type Path<T> = T extends string
  ? T
  : {
      [K in keyof T]: T[K] extends string
        ? T[K]
        : T[K] extends object
          ? `${Path<T[K]>}`
          : never;
    }[keyof T];

export type ModalRoute = Flatten<Path<typeof MODAL_ROUTES>>;

export const ROLES = {
  SUPER_ADMIN: {
    value: "super-admin",
    label: "Super Admin",
    description:
      "A super admin has access to all permissions in the admin dashboard including editing user type.",
  },
  ADMIN: {
    value: "admin",
    label: "Admin",
    description:
      "An admin has access to all permissions in the admin dashboard excluding editing user type.",
  },
  STAFF: {
    value: "staff",
    label: "Staff",
    description:
      "A staff user has access to the admin dashboard, can send password resets and book visits on behalf of patients.",
  },
  USER: {
    value: "user",
    label: "User",
    description: "A user does not have access to the admin dashboard.",
  },
} as const;

export type RoleKey = keyof typeof ROLES;
export type Role = (typeof ROLES)[RoleKey]["value"];

const roleByValue = Object.entries(ROLES).reduce(
  (acc, [key, data]) => {
    acc[data.value] = { key: key as RoleKey, ...data };
    return acc;
  },
  {} as Record<Role, { key: RoleKey } & (typeof ROLES)[RoleKey]>,
);

export const getRoleLabel = (role: Role): string => roleByValue[role].label;
export const getRoleDescription = (role: Role): string =>
  roleByValue[role].description;

export const INTAKE_FORM_STATUS = {
  PENDING: "pending",
  SUBMITTED: "submitted",
  NOT_REQUIRED: "not_required",
} as const;

export type IntakeFormStatus =
  (typeof INTAKE_FORM_STATUS)[keyof typeof INTAKE_FORM_STATUS];

export interface IntakeFormSubmission {
  id: number;
  submissionId: string;
  submissionDate: string | null;
}

export interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  userType: Role;
  joinedDate: string | null;
  lastLoginDate: string | null;
  lastPasswordUpdateDate: string | null;
  intakeFormSubmission?: IntakeFormSubmission;
  intakeFormStatus: IntakeFormStatus;
  showWelcomeVideo: boolean;
  source: Source;
  isTest: boolean;
  stateId?: number;
  ehrId?: string | null;
  setPasswordDate: string | null;
  dateOfBirth?: string | null;
  sexAtBirth?: "M" | "F" | null;
  phoneNumber?: string | null;
  patientRegistrations?: PatientRegistration[];
}

export interface PatientRegistration {
  patientId: string | null;
  isPrimary: boolean;
  providerGroup: {
    id: string;
    name: string;
  };
  states: string[];
}

export const SOURCE = {
  UHC: "UHC",
  MGBHP: "MGBHP",
  EMBLEM_EMPLOYEES: "EmblemEmployees",
  EMBLEM_ILS: "EmblemILS",
  BEANSTALK: "Beanstalk",
  QUICKJOIN: "QuickJoin",
  DIRECT: "Direct",
  ACTIVITY_PARTNERSHIP: "ActivityPartnership",
  LEGACY: "Legacy",
  NO_FORM: "NoForm",
} as const;

export type Source = (typeof SOURCE)[keyof typeof SOURCE];

export interface Resource {
  id: number;
  title: string;
  link: string;
  content?: {
    imageUrl?: string;
    iconUrl?: string;
    thumbnailUrl?: string;
    subtitle?: string;
  };
}

export interface ResourceGroup {
  group: string;
  resources: Resource[];
}

export interface TimeSlot {
  id: number;
  duration: number;
  startTime: string;
  originalTime?: string;
  providerId?: number;
  providerName?: string;
  providerTitle?: string;
}

export interface ProviderSlots {
  [date: string]: TimeSlot[];
}

export interface Provider {
  id: number;
  name: string;
  title: string;
  description: string;
  image: string | null;
  slots: ProviderSlots;
}

export type CalendarDay = {
  date: Date;
  dayOfWeek: string;
  dayNumber: number;
  isToday: boolean;
  isTomorrow: boolean;
  isAvailable: boolean;
};

export type MonthYear = {
  month: string;
  year: number;
};

export type TimeZone = {
  code: string;
  name: string;
  city: string;
  iana: string;
};

export interface ComponentTimeSlot {
  id: string;
  time: string;
}

export interface ComponentProvider {
  id: string;
  name: string;
  title: string;
  description: string;
  image: string;
  availableSlots: ComponentTimeSlot[];
}

export const INSURANCE_NOT_FOUND_OPTION = {
  value: -1,
  label: "My Insurance is not listed",
};

export const OTHER_STATES_OPTIONS = {
  OTHER: {
    value: -1,
    label: "Other",
  },
  VERMONT: {
    value: -2,
    label: "Vermont",
  },
} as const;
